import { withRetries } from "./with-retries";
import { ConsolaInstance } from "consola";

export type PreEstimationResult<C> = {
	estimateGas: () => Promise<bigint>;
	calldata: () => Promise<C>;
	onPacked?: () => void;
};

export type BatchResultItem<T, C> = {
	calldata: () => Promise<C>;
	gas_limit: bigint;
	items_used: T[];
};

export type BatchByGasLimitParams<T, C> = {
	logger?: ConsolaInstance;
	input_data: T[];
	preEstimationHook: (sliced_input_data: T[]) => Promise<PreEstimationResult<C>>;
	max_gas_limit: number;
	max_full_cycles?: number; // use it if you want to stop after N full loops
};

export type BatchByGasLimitResult<T, C> = {
	batches: BatchResultItem<T, C>[];
	broken_items: T[];
	cycles: number;
};

export async function batchByGasLimitOptimistic<T, C>(
	params: BatchByGasLimitParams<T, C>
): Promise<BatchByGasLimitResult<T, C>> {
	const { input_data, preEstimationHook, max_gas_limit, logger, max_full_cycles } = params;

  if(input_data.length === 0) {
    return { batches: [], broken_items: [], cycles: 0 };
  }

	// Cache for gas estimations: key = "startIndex-endIndex", value = estimation result
	type CachedEstimation = {
		gas_limit: bigint;
		onPacked?: () => void;
		calldata: () => Promise<C>;
	};

	type FailedEstimation = {
		gas_limit: false;
		onPacked?: () => void;
		calldata: () => Promise<C>;
		last_error: unknown;
	};

	const estimation_cache = new Map<string, CachedEstimation>();

  async function runEstimation(chunk: T[], start_index: number) {
    const cache_key = `${start_index}-${start_index + chunk.length - 1}`;

    if (estimation_cache.has(cache_key)) {
      // logger?.debug(`Cache hit for batch ${cache_key}`);
      const cached_result = estimation_cache.get(cache_key)!;
      return { gas_limit: cached_result.gas_limit, onPacked: cached_result.onPacked, calldata: cached_result.calldata };
    }

    const { estimateGas, calldata, onPacked } = await preEstimationHook(chunk);

    let last_error: unknown = null;
    const gas_limit: bigint | false = await withRetries(() => estimateGas(), 3).catch(err => {
      last_error = err;
      // logger?.warn(`Gas estimation failed for chunk with size ${chunk.length}:`, err);
      return false;
    });

    const result = gas_limit === false
      ? {
          gas_limit,
          onPacked,
          calldata,
          last_error,
        } satisfies FailedEstimation
      : {
          gas_limit,
          onPacked,
          calldata,
        };

    // Cache successful estimations
    if (gas_limit !== false) {
      estimation_cache.set(cache_key, { gas_limit, onPacked, calldata });
      // logger?.debug(`Cached estimation for batch ${cache_key}: ${gas_limit.toString()}`);
    }

    return result;
  }

	async function findOptimalBatchSize(
		remaining_data: T[],
		start_index: number
	): Promise<{ batch_size: number; last_estimation: CachedEstimation }> {
    if (remaining_data.length === 0) throw new Error("Cannot find batch size for empty data");
    let last_failed_estimation_error: unknown = null;

    // Start by testing the maximum possible size first
    // logger?.debug(`Testing maximum batch size: ${remaining_data.length}`);
    const max_test = await runEstimation(remaining_data, start_index);
    if (max_test.gas_limit !== false && max_test.gas_limit <= max_gas_limit) {
      return { batch_size: remaining_data.length, last_estimation: { gas_limit: max_test.gas_limit, onPacked: max_test.onPacked, calldata: max_test.calldata } };
    }
    if (max_test.gas_limit === false) {
      last_failed_estimation_error = max_test.last_error;
    }

		// Binary search from max down to find optimal size
		let left = 1;
		let right = remaining_data.length - 1;
		let optimal_size = 0;
		let last_successful_estimation: CachedEstimation | null = null;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const test_chunk = remaining_data.slice(0, mid);

      // logger?.debug(`Binary search: testing batch size ${mid} (range: ${left}-${right})`);

      const estimation_result = await runEstimation(test_chunk, start_index);

      if (estimation_result.gas_limit !== false && estimation_result.gas_limit <= max_gas_limit) {
        optimal_size = mid;
        last_successful_estimation = {
          gas_limit: estimation_result.gas_limit,
          onPacked: estimation_result.onPacked,
          calldata: estimation_result.calldata
        };
        left = mid + 1;
      } else {
        if (estimation_result.gas_limit === false) {
          last_failed_estimation_error = estimation_result.last_error;
        }
        right = mid - 1;
      }
    }

    if (optimal_size === 0 || !last_successful_estimation) {
      const error_message = last_failed_estimation_error instanceof Error
        ? last_failed_estimation_error.message
        : String(last_failed_estimation_error ?? "unknown gas estimation failure");
      throw new Error(`Binary search failed to find valid batch size. Last estimation error: ${error_message}`);
    }

    return { batch_size: optimal_size, last_estimation: last_successful_estimation };
  }

	const batches: BatchResultItem<T, C>[] = [];
	const broken_items: T[] = [];
	let current_index = 0;
	let cycles = 0;

  while (current_index < input_data.length) {

    if(max_full_cycles && cycles >= max_full_cycles) {
      break;
    }

    const remaining_data = input_data.slice(current_index);

    try {
      const { batch_size: optimal_batch_size, last_estimation } = await findOptimalBatchSize(remaining_data, current_index);

      const batch_chunk = remaining_data.slice(0, optimal_batch_size);

      batches.push({
        calldata: last_estimation.calldata,
        gas_limit: last_estimation.gas_limit,
        items_used: batch_chunk,
      });

      last_estimation.onPacked && last_estimation.onPacked();
      current_index += optimal_batch_size;

      // logger?.log(`Packed batch of ${optimal_batch_size} items (gas: ${last_estimation.gas_limit.toString()})`);
    } catch (error) {
      const broken_item = input_data[current_index];
      broken_items.push(broken_item);
      if(error instanceof Error) {
      	logger?.error(`Item at index ${current_index} is broken, skipping it: ${error.message}`);
      }
      current_index++;
    }

    cycles++;
  }

  return { batches, broken_items, cycles };
}
